import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { setChecklist, setView } from './onboardingSlice';
import { fetchChecklist, startRapydAgreement } from './checklistApi';

const RapydAgreement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { applicationId, brandConfig, checklist } = useSelector((s: RootState) => s.onboarding);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const done = checklist?.rapyd_agreement_status === 'COMPLETED';

  useEffect(() => {
    if (!applicationId || done) return undefined;

    let cancelled = false;
    const pull = async () => {
      try {
        const row = await fetchChecklist(applicationId);
        if (cancelled) return;
        dispatch(setChecklist(row));
        if (row.rapyd_agreement_status === 'COMPLETED') dispatch(setView('hub'));
      } catch (_) { /* keep polling */ }
    };

    pull();
    const id = window.setInterval(pull, 2000);
    const onFocus = () => { pull(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [applicationId, dispatch, done]);

  const open = async () => {
    if (!applicationId) {
      setError('Submit the onboarding form first.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const { application, signUrl } = await startRapydAgreement(applicationId, brandConfig);
      dispatch(setChecklist(application));
      if (signUrl) window.open(signUrl, '_blank', 'noopener');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not open the Rapyd agreement');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Button variant="text" onClick={() => dispatch(setView('hub'))} sx={{ mb: 1, px: 0 }}>← Back to overview</Button>
      <Typography sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 700, fontSize: 28, mb: 1 }}>
        Rapyd merchant agreement
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 2 }}>
        Complete the merchant company and contact details in Zoho Sign, then sign and date the merchant section.
        This page updates automatically once the signed document is received.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {done ? (
        <Alert severity="success">Agreement signed. You can continue to Direct Debit.</Alert>
      ) : (
        <>
          <Button variant="contained" onClick={open} disabled={busy}>
            {busy ? 'Opening…' : 'Open Rapyd agreement'}
          </Button>
          {checklist?.rapyd_agreement_status === 'IN_PROGRESS' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Waiting for Zoho to confirm the signature. This page updates automatically — no refresh needed.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default RapydAgreement;
